/** Canonical model IDs used with the Keywords adapter */
export const MODELS = {
    OPUS_4_6: "anthropic/claude-opus-4-6",
    SONNET_4_5: "anthropic/claude-sonnet-4-5",
    HAIKU_4_5: "anthropic/claude-haiku-4-5",
    GEMINI_3_PRO: "gemini/gemini-3-pro-preview",
    GEMINI_3_FLASH: "gemini/gemini-3-flash-preview",
};
/** The default model used across the application */
export const DEFAULT_MODEL = MODELS.OPUS_4_6;
/** Set of all valid model IDs (for validation / migration) */
export const VALID_MODEL_IDS = new Set(Object.values(MODELS));
/**
 * Maximum output tokens per model.
 * Used by the adapter to set max_tokens on each request.
 *
 * Sources:
 *   Claude Opus 4.6:   128K  (https://docs.anthropic.com/en/docs/about-claude/models)
 *   Claude Sonnet 4.5: 64K
 *   Claude Haiku 4.5:  64K
 *   Gemini 3 Pro:      65536 (https://ai.google.dev/gemini-api/docs/models)
 *   Gemini 3 Flash:    65536
 */
export const MODEL_MAX_OUTPUT_TOKENS = {
    [MODELS.OPUS_4_6]: 128000,
    [MODELS.SONNET_4_5]: 64000,
    [MODELS.HAIKU_4_5]: 64000,
    [MODELS.GEMINI_3_PRO]: 65536,
    [MODELS.GEMINI_3_FLASH]: 65536,
};
/** Fallback max output tokens for unknown models */
export const DEFAULT_MAX_OUTPUT_TOKENS = 16384;
/**
 * Context window sizes per model (input tokens).
 *
 * Claude Opus 4.6 and Sonnet 4.5 support 1M with the beta header.
 * Gemini models natively support ~1M context.
 */
export const MODEL_CONTEXT_WINDOW = {
    [MODELS.OPUS_4_6]: 1000000, // 1M with anthropic-beta header
    [MODELS.SONNET_4_5]: 1000000, // 1M with anthropic-beta header
    [MODELS.HAIKU_4_5]: 200000,
    [MODELS.GEMINI_3_PRO]: 1048576,
    [MODELS.GEMINI_3_FLASH]: 1048576,
};
/** Default context window for unknown models */
export const DEFAULT_CONTEXT_WINDOW = 200000;
/**
 * Returns true if the given model requires the Anthropic 1M context beta header.
 */
export function needsAnthropicContextBetaHeader(modelId) {
    return modelId === MODELS.OPUS_4_6 || modelId === MODELS.SONNET_4_5;
}
/**
 * The beta header value for enabling 1M context on Claude.
 */
export const ANTHROPIC_1M_BETA_HEADER = "context-1m-2025-08-07";
/**
 * Get the max output tokens for a model (falls back to DEFAULT_MAX_OUTPUT_TOKENS).
 */
export function getMaxOutputTokens(modelId) {
    return MODEL_MAX_OUTPUT_TOKENS[modelId] ?? DEFAULT_MAX_OUTPUT_TOKENS;
}
/**
 * Get the context window for a model (falls back to DEFAULT_CONTEXT_WINDOW).
 */
export function getContextWindow(modelId) {
    return MODEL_CONTEXT_WINDOW[modelId] ?? DEFAULT_CONTEXT_WINDOW;
}
